import { cached, tracked } from '@glimmer/tracking';

import {
	Aggregation,
	BlockVisualization,
	ChartLegendPosition,
	DataSelectType,
	OrderDirection,
	ShownLabels,
} from 'webapp/enums/enums';
import { getAggregationLabel, hashCode, useAggregationOnValues } from 'webapp/utils/number';

import type { AnyVisualization } from 'webapp/models-custom/block-setup';
import type DataSelect from 'webapp/models-custom/data-select';
import type DsDataPointOther from 'webapp/models-custom/ds-data/point/other';
import VisualizationBase from 'webapp/models-custom/visualization/base';
import DChartPrimaryPoint from 'webapp/d-chart/point/primary-point';
import DChartSeries from 'webapp/d-chart/series/series';

interface AggregationPoint {
	value: number | null;
	label: string;
}

const RecordLimit = 15;

export default class VisualizationPie extends VisualizationBase {
	visualizationType = BlockVisualization.PIE;

	recordLimit = RecordLimit;

	get allowOthersSlice() {
		return !!this.block.showOther;
	}

	get isLegendOff(): boolean {
		return this.block.legendPosition === ChartLegendPosition.OFF;
	}

	get isLegendOutside(): boolean {
		return !this.isLegendOff && !this.isLegendBottom;
	}

	get isLegendBottom(): boolean {
		return this.block.legendPosition === ChartLegendPosition.BOTTOM;
	}

	get showValues() {
		return this.blockSetup.firstShownDataSelect?.shownLabels === ShownLabels.VALUE;
	}

	get aggregation() {
		return this.blockSetup.dataSelectsShown[0]?.aggregation || null;
	}

	// ------ Data ------

	@tracked series: DChartSeries | null = null;

	@tracked aggregationPoint: AggregationPoint | null = null;

	override updateDataCache() {
		this.series = this.getSeries();
		this.aggregationPoint = this.getAggregationPoint();
		this.updateColorIndexes();
	}

	override clearDataCache() {
		this.series = null;
		this.aggregationPoint = null;
	}

	updateColorIndexes() {
		const points = this.series?.points || [];
		this.updateColorIndexesForItems(points);
	}

	@cached
	get allPoints() {
		const points: DChartPrimaryPoint[] = [];

		this.blockSetup.sortedDataSelectsShown.forEach((ds) => {
			ds.outputs.forEach((output) => {
				output.points.forEach((dsDataPoint) => {
					const point = this.createDChartPointFromPoint(output, dsDataPoint);
					point.index = points.length;
					points.push(point);
				});
			});
		});
		return points;
	}

	@cached
	get otherPointValue() {
		let otherSum = 0;
		if (this.allowOthersSlice) {
			this.blockSetup.sortedDataSelectsShown.forEach((ds) => {
				ds.dataRoot?.outputsOther.forEach((otherOutput) => {
					const pointTotal = otherOutput.pointTotal;
					if (pointTotal) {
						otherSum += pointTotal.value || 0;
					}
				});
			});
		}
		return otherSum;
	}

	private getAggregationPoint(): AggregationPoint | null {
		const aggregation = this.aggregation;

		if (!aggregation) {
			// turned off
			return null;
		}

		const allPointsValues: number[] = [],
			otherPoints: DsDataPointOther[] = [];

		this.allPoints.forEach((p) => {
			if (p.hasValue) {
				allPointsValues.push(p.value!);
			}
		});
		let otherCount = 0;
		this.blockSetup.sortedDataSelectsShown.forEach((ds) => {
			ds.dataRoot?.outputsOther.forEach((otherOutput) => {
				otherPoints.push(...otherOutput.points);
				otherCount += otherOutput.count;
			});
		});

		const value = useAggregationOnValues(aggregation, allPointsValues, otherPoints, otherCount),
			label = getAggregationLabel(aggregation);

		return {
			value,
			label,
		};
	}

	private getSeries() {
		// Just one series, all primary points together
		// Each point matches an output

		const points = Array.from(this.allPoints);

		// sorting the points
		const sortField = this.block.sortField,
			sortOrder = this.block.sortOrder || OrderDirection.DESC;
		points.sort((a, b) => {
			return DChartPrimaryPoint.doPointCompare(a, b, sortField, sortOrder);
		});

		const series = new DChartSeries({
			type: DataSelectType.MAIN,
			timezone: this.dataTimezone,
			points,
		});

		return series;
	}

	// ------- general helpers --------

	override onCreatePrimaryMainDataSelect(dataSelect: DataSelect): void {
		super.onCreatePrimaryMainDataSelect(dataSelect);

		// copy the same aggregation
		dataSelect.aggregation = this.aggregation;
	}

	// ----------- Detecting possible rendering changes -----------

	override get visualizationSettingsHash(): number {
		const block = this.block,
			dss = this.blockSetup.dataSelects;
		return hashCode(
			JSON.stringify({
				lPos: block.legendPosition,
				sw: this.showValues,
				sf: block.sortField,
				so: block.sortOrder,
				bc: block.colors,
				a: dss.map((ds) => ds.aggregation), // middle number
				o: dss.map((ds) => ds.order), // bcos of possible custom sort
			}),
		);
	}

	// ------- visualization switch helpers --------
	override updateForSwitchFrom(visualizationFrom: AnyVisualization): void {
		super.updateForSwitchFrom(visualizationFrom);
		// set SUM if the block we're switching from does not have aggregation setting
		if (!visualizationFrom.showAggregationChoiceOnBlock) {
			const dataSelectsShown = this.blockSetup.dataSelectsShown;
			dataSelectsShown.forEach((ds) => {
				ds.aggregation = Aggregation.SUM;
			});
		}
	}

	// ------- new visualization creation helpers -------

	override doAfterNewBlockCreatedActivities() {
		// at this point, the block will be already saved and have the necessary dataSelects
		this.block.legendPosition = ChartLegendPosition.OUTSIDE;

		const dataSelectsShown = this.blockSetup.dataSelectsShown;
		dataSelectsShown.forEach((ds) => {
			ds.aggregation = Aggregation.SUM;
		});

		return super.doAfterNewBlockCreatedActivities();
	}

	// ------- MISC --------

	override getAllMainPoints() {
		const points: DChartPrimaryPoint[] = [];

		points.push(...(this.series?.points || []));

		return points;
	}

	static override recordLimit = RecordLimit;
}
